import { useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";

export default function Prodotti() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categoryOpen, setCategoryOpen] = useState(false);

  const load = () => api.get("/products").then(({ data }) => setRows(data));
  const loadCategories = () => api.get("/categories", { params: { kind: "prodotto" } }).then(({ data }) => setCategories(data));

  useEffect(() => { load(); loadCategories(); }, []);

  const FIELDS = [
    { name: "code", label: "Codice", required: true },
    { name: "name", label: "Descrizione", required: true },
    {
      name: "category", label: "Categoria", type: "select",
      options: categories.map((c) => ({ value: c.name, label: c.name })),
    },
    {
      name: "kind", label: "Tipo", type: "select",
      options: [
        { value: "prodotto", label: "Prodotto" },
        { value: "ricambio", label: "Ricambio" },
        { value: "servizio", label: "Servizio" },
      ],
    },
    { name: "price", label: "Prezzo vendita €", type: "number" },
    { name: "cost", label: "Costo €", type: "number" },
    { name: "rental_price", label: "Canone noleggio €/mese", type: "number" },
    { name: "stock", label: "Giacenza", type: "number" },
    { name: "min_stock", label: "Soglia minima", type: "number" },
    { name: "reorder_qty", label: "Quantità di riordino", type: "number" },
    { name: "supplier", label: "Fornitore", full: true },
    { name: "check_interval_months", label: "Verifica ogni (mesi)", type: "number" },
  ];

  const categoryFields = [
    { name: "name", label: "Nome categoria", required: true, full: true },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/products/${editing.id}`, values);
      else await api.post("/products", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const saveCategory = async (values) => {
    try {
      await api.post("/categories", { ...values, kind: "prodotto" });
      await loadCategories();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="prodotti-page">
      <PageHeader title="Prodotti e ricambi" subtitle={`${rows.length} articoli`}>
        {user?.role === "azienda" && (
          <>
            <Button variant="outline" className="h-11 rounded-sm" onClick={() => setCategoryOpen(true)}
                    data-testid="new-category-btn">
              <Tag className="h-4 w-4 mr-2" /> Nuova categoria
            </Button>
            <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-product-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuovo articolo
            </Button>
          </>
        )}
      </PageHeader>

      <ListCard testid="prodotti-list">
        {rows.map((p) => (
          <div key={p.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
                <p className="font-semibold">{p.name}</p>
                <Badge variant="secondary" className="rounded-sm capitalize">{p.kind}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Vendita {eur(p.price)} · Noleggio {eur(p.rental_price)}/mese · Giacenza {p.stock} {p.unit}
              </p>
            </div>
            {user?.role === "azienda" && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="rounded-sm"
                        onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-product-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                        onClick={async () => { await api.delete(`/products/${p.id}`); load(); }}
                        data-testid={`delete-product-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun articolo." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica articolo" : "Nuovo articolo"}
                  fields={FIELDS} initial={editing || { kind: "prodotto" }} onSubmit={save} testid="product-dialog" />
      <FormDialog open={categoryOpen} onOpenChange={setCategoryOpen} title="Nuova categoria"
                  fields={categoryFields} initial={{}} onSubmit={saveCategory} testid="category-dialog" />
    </div>
  );
}
